import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';
import {
  getArticleCategoriesInMenu,
  getPagedArticleByTag,
  getTagBySlug,
} from 'services/cmsService';
import { IArticleResult } from 'types/pages';
import { tryToNoticeError } from 'utils/errorInfo';

import Typography from '@material-ui/core/Typography';
import Pagination from '@material-ui/lab/Pagination';

import ArticleListSection from 'components/CMS/ArticleListSection';
import { ArticleResultLanding } from 'components/CMS/ArticleResultLanding';

import { ContentHubContainer } from 'modules/ContentHubContainer';

import styles from './style.module.scss';

export const ARTICLE_RESULT_FIRST_PAGE_AMOUNT = 14;
export const ARTICLE_RESULT_REST_PAGES_AMOUNT = 18;

const ArticlesByTag = (props: IArticleResult) => {
  const router = useRouter();

  const {
    articles,
    categoriesOnMenu,
    title,
    query,
    pageCount,
    promotion,
    metaTitle,
    metaDescription,
  } = props;

  return (
    <ContentHubContainer categories={categoriesOnMenu}>
      <NextSeo
        title={metaTitle ?? undefined}
        description={metaDescription ?? undefined}
        noindex={true}
        nofollow={true}
        openGraph={{
          title: metaTitle ?? undefined,
          description: metaDescription ?? undefined,
          type: 'article',
        }}
      />

      <Typography component="h1" variant="h4" color="secondary" classes={{ h4: styles.header }}>
        #{title}
      </Typography>

      {query.page === 1 && <ArticleResultLanding articles={articles} promotion={promotion} />}
      {query.page > 1 && <ArticleListSection articles={articles} />}
      {pageCount > 1 && (
        <div className={styles.pagination}>
          <Pagination
            count={pageCount}
            page={+query.page || 1}
            variant="outlined"
            shape="rounded"
            color="primary"
            onChange={(e, value) => {
              query.page = value;
              router.push({ pathname: location.pathname, query });
            }}
          />
        </div>
      )}
    </ContentHubContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<IArticleResult>> {
  try {
    const { slug, page = '1' } = ctx.query;
    const query = {
      page: page ? +page : 1,
    };
    const isFirstPage = page == '1';
    const currentPage = isFirstPage ? 1 : parseInt(page.toString(), 10);
    const start = isFirstPage
      ? 0
      : ARTICLE_RESULT_FIRST_PAGE_AMOUNT + (currentPage - 2) * ARTICLE_RESULT_REST_PAGES_AMOUNT;

    const [{ articles, total }, categories, tag] = await Promise.all([
      getPagedArticleByTag(
        slug as string,
        isFirstPage ? ARTICLE_RESULT_FIRST_PAGE_AMOUNT : ARTICLE_RESULT_REST_PAGES_AMOUNT,
        start,
      ),
      getArticleCategoriesInMenu(),
      getTagBySlug(slug),
    ]);
    if (!tag) {
      return {
        notFound: true,
      };
    }

    const totalWithoutFirstPage = total - ARTICLE_RESULT_FIRST_PAGE_AMOUNT;
    const pageCount = Math.ceil(totalWithoutFirstPage / ARTICLE_RESULT_REST_PAGES_AMOUNT) + 1;

    return {
      props: {
        slug: slug as string,
        query,
        pageCount,
        articles: articles ?? [],
        categoriesOnMenu: categories,
        title: tag.name,
        promotion: tag.article_promotion,
        metaTitle: tag.meta_title,
        metaDescription: tag.meta_description,
      },
    };
  } catch (error) {
    const errorMessage = `Article Tag Data Error: ${error.message}`;
    tryToNoticeError(new Error(errorMessage, { cause: error }), { error });
    return { notFound: true };
  }
}

export default ArticlesByTag;
